// Graph diagnostic panel. Admin-only — surfaces the live state of
// the Microsoft Graph integration so an operator can debug why
// PersonSelect / member-add isn't finding tenant users without
// shelling into App Service. Mounted from the bell-icon menu in
// the planner header.
import { useEffect, useState } from "react";
import { CheckCircle2, RefreshCw, XCircle } from "lucide-react";
import { Sheet } from "../components/Sheet";
import { tpApi } from "../api";
import { DC } from "./createSlideOver/primitives";

type Diag = Awaited<ReturnType<typeof tpApi.graphDiag>>;

export function GraphDiagPanel({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [data, setData] = useState<Diag | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    void tpApi
      .graphDiag()
      .then((d) => setData(d))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [open, tick]);

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Graph user search diagnostics"
      footer={
        <button
          type="button"
          className="bsp-btn bsp-btn-secondary bsp-btn-sm"
          onClick={() => setTick((n) => n + 1)}
        >
          <RefreshCw size={12} /> Re-run
        </button>
      }
    >
      {loading ? (
        <p style={{ color: DC.grey }}>Running diagnostics…</p>
      ) : error ? (
        <div style={errorBox}>
          <strong>Diag request failed:</strong> {error}
          <div style={{ fontSize: 12, marginTop: 6 }}>
            This usually means you're not signed in as a ToolsHub.Admin —
            the endpoint is admin-only.
          </div>
        </div>
      ) : !data ? null : (
        <>
          <Section title="Server config">
            <Check label="GRAPH_TENANT_ID set" ok={data.config.hasTenantId} />
            <Check label="GRAPH_CLIENT_ID set" ok={data.config.hasClientId} />
            <Check label="GRAPH_CLIENT_SECRET set" ok={data.config.hasClientSecret} />
            <Check
              label="All three env vars present"
              ok={data.config.configured}
              hint={
                data.config.configured
                  ? "Server is configured to call Graph."
                  : "Add the missing env vars in App Service → Configuration → Application settings, then restart the slot."
              }
            />
          </Section>

          <Section title="Local planner_users">
            <div style={{ fontSize: 13 }}>
              <strong>{data.localUserCount}</strong> row
              {data.localUserCount === 1 ? "" : "s"} in the planner_users
              projection.
            </div>
            <p style={{ fontSize: 12, color: DC.grey, margin: "6px 0 0" }}>
              Anyone who has signed into the planner before lands here
              automatically. A brand-new tenant starts at zero, so the
              picker depends entirely on Graph until people log in.
            </p>
          </Section>

          <Section title="Live Graph test (searches “a”)">
            {!data.config.configured ? (
              <p style={{ fontSize: 12, color: DC.grey, margin: 0 }}>
                Skipped — Graph isn't configured.
              </p>
            ) : data.testSearch?.ok ? (
              <Check
                label={`Graph returned ${data.testSearch.hitCount ?? 0} candidate${
                  data.testSearch.hitCount === 1 ? "" : "s"
                }`}
                ok
                hint="Search is working. If PersonSelect still looks empty, you may need to type 2+ characters before hits appear."
              />
            ) : data.testSearch?.error ? (
              <>
                <Check label="Graph rejected the test call" ok={false} />
                <pre style={errorCodeBox}>
                  {data.testSearch.error.code
                    ? `${data.testSearch.error.code}\n\n`
                    : ""}
                  {data.testSearch.error.message}
                  {data.testSearch.error.status
                    ? `\n\nHTTP status: ${data.testSearch.error.status}`
                    : ""}
                </pre>
                <p style={{ fontSize: 12, color: DC.grey, margin: "8px 0 0" }}>
                  Common error codes:
                  <br />
                  • <strong>Authorization_RequestDenied</strong> — User.ReadBasic.All wasn't granted, or admin consent wasn't clicked. Re-check the API permissions tab on the app registration.
                  <br />
                  • <strong>invalid_client</strong> — client secret expired or wrong. Mint a new one and update GRAPH_CLIENT_SECRET.
                  <br />
                  • <strong>AADSTS70011</strong> — wrong tenant id. Check GRAPH_TENANT_ID matches AZURE_TENANT_ID.
                </p>
              </>
            ) : null}
          </Section>
        </>
      )}
    </Sheet>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 18 }}>
      <div
        style={{
          font: "700 10px/1 var(--bsp-font-sans)",
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: DC.grey,
          marginBottom: 8,
        }}
      >
        {title}
      </div>
      {children}
    </section>
  );
}

function Check({ label, ok, hint }: { label: string; ok: boolean; hint?: string }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
      {ok ? (
        <CheckCircle2 size={16} color="var(--bsp-green)" />
      ) : (
        <XCircle size={16} color="var(--bsp-red)" />
      )}
      <div>
        <div style={{ fontSize: 13 }}>{label}</div>
        {hint ? (
          <div style={{ fontSize: 11, color: DC.grey, marginTop: 2 }}>{hint}</div>
        ) : null}
      </div>
    </div>
  );
}

const errorBox: React.CSSProperties = {
  padding: 12,
  background: "var(--bsp-red-tint)",
  border: "1px solid var(--bsp-red)",
  borderRadius: 6,
  color: "var(--bsp-red-fg)",
  fontSize: 13,
  lineHeight: 1.4,
};

const errorCodeBox: React.CSSProperties = {
  margin: "8px 0 0",
  padding: 10,
  background: "var(--bsp-surface-muted)",
  border: "1px solid var(--bsp-border)",
  borderRadius: 4,
  fontSize: 12,
  whiteSpace: "pre-wrap",
  fontFamily: "var(--bsp-font-mono)",
};
